import * as React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader,
  DialogTitle, DiscardGuard,
} from "@/components/ui/dialog";
import { Callout } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { AudioPicker } from "@/features/audio-picker";
import { ApiError } from "@/lib/api";
import { isCurrentStage, stageSlugs, stageText } from "@/lib/contacts";
import { useAuth } from "@/lib/auth";
import { useFormChanged, zodResolver } from "@/lib/form";
import { useCreateVoicemailMessage, useTenants, useUpdateVoicemailMessage } from "@/lib/queries";
import type { VoicemailMessage } from "@/lib/types";
import { cn } from "@/lib/utils";
import {
  NAME_MAX, NEW_VOICEMAIL_MESSAGE, VOICEMAIL_LANGUAGES, VOICEMAIL_SLUG_RE, languageText,
  newVoicemailMessagePayload, voicemailMediaProblem, voicemailMessageChanges, voicemailMessageValues,
} from "@/lib/voicemail";

/**
 * Add a message to the voicemail library, or edit one (ADR-019).
 *
 * The slug is typed once: a rep's drop names the message by it, so core-api has no way to rename
 * one. The recording is a file chosen from the audio store (`AudioPicker`), and there is no saving
 * without one, because a message has no default to fall back on. An edit sends only what changed
 * (`voicemailMessageChanges`). Retiring is not on the form either: it is the list's own action, with
 * a confirmation of its own, because it takes the message off every rep's phone.
 */

/** Radix's Select cannot hold `""`, so "no language" needs a value of its own. */
const NONE = "none";

const schema = z.object({
  slug: z
    .string()
    .min(1, "Required")
    .regex(VOICEMAIL_SLUG_RE, "Lowercase letters, digits, underscore and hyphen, up to 63"),
  name: z
    .string()
    .trim()
    .min(1, "Required")
    .max(NAME_MAX, `At most ${NAME_MAX} characters`),
  language: z.string(),
  stage: z.string(),
  media: z.string().superRefine((value, ctx) => {
    const problem = voicemailMediaProblem(value);
    if (problem) ctx.addIssue({ code: "custom", message: problem });
  }),
  tenant_id: z.number().nullable(),
});
type FormValues = z.infer<typeof schema>;

const BLANK: FormValues = { ...NEW_VOICEMAIL_MESSAGE, tenant_id: null };

export function VoicemailMessageFormDialog({
  open, onOpenChange, message,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Absent for create. */
  message?: VoicemailMessage;
}) {
  const { user: actor } = useAuth();
  const toast = useToast();
  const create = useCreateVoicemailMessage();
  const update = useUpdateVoicemailMessage();
  // Only a superadmin names a tenant, and only while there are several: with one, core-api fills it
  // in, and everyone else's is their own whatever they send.
  const isSuperadmin = actor?.role === "superadmin";
  const { data: tenants } = useTenants({ enabled: open && isSuperadmin && !message });
  const pickTenant = isSuperadmin && !message && (tenants?.length ?? 0) > 1;
  const [formError, setFormError] = React.useState<string | null>(null);

  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: BLANK });
  const { errors } = form.formState;
  const changed = useFormChanged(form);

  React.useEffect(() => {
    if (!open) return;
    setFormError(null);
    form.reset(message ? { ...voicemailMessageValues(message), tenant_id: message.tenant_id } : BLANK);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, message]);

  const values = form.watch();
  const set = <K extends keyof FormValues>(key: K, value: FormValues[K]) =>
    form.setValue(key, value as never, { shouldDirty: true, shouldValidate: true });

  const onSubmit = form.handleSubmit(async (submitted) => {
    setFormError(null);
    if (pickTenant && submitted.tenant_id === null) {
      form.setError("tenant_id", { message: "Choose the tenant whose library it joins" });
      return;
    }
    try {
      if (message) {
        const changes = voicemailMessageChanges(submitted, message);
        if (Object.keys(changes).length > 0) {
          await update.mutateAsync({ id: message.id, ...changes });
        }
        toast.success("Message saved", submitted.name);
      } else {
        await create.mutateAsync({
          ...newVoicemailMessagePayload(submitted),
          ...(isSuperadmin && submitted.tenant_id !== null ? { tenant_id: submitted.tenant_id } : {}),
        });
        toast.success("Message added", submitted.name);
      }
      onOpenChange(false);
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Could not save the message.");
    }
  });

  const knownLanguage = (VOICEMAIL_LANGUAGES as readonly string[]).includes(values.language);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer">
        <DiscardGuard changed={changed} />
        <DialogHeader>
          <DialogTitle>{message ? "Edit voicemail message" : "New voicemail message"}</DialogTitle>
          <DialogDescription>
            {message
              ? "What reps see it called, and what it plays."
              : "A recording a rep can leave when a dialer call reaches a lead's voicemail. Reps find it under Transfer."}
          </DialogDescription>
        </DialogHeader>

        <DialogForm onSubmit={onSubmit} noValidate>
          <DialogBody className="space-y-4">
            {formError && <Callout tone="danger">{formError}</Callout>}

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Name" htmlFor="voicemail-name" required error={errors.name?.message}>
                <Input
                  autoComplete="off"
                  placeholder="Please call us back"
                  invalid={Boolean(errors.name)}
                  {...form.register("name")}
                />
              </Field>
              <Field
                label="Slug"
                htmlFor="voicemail-slug"
                required={!message}
                error={errors.slug?.message}
                hint={
                  message
                    ? "Fixed. A drop names the message by it."
                    : "What a drop names the message by. It cannot be changed later."
                }
              >
                <Input
                  // Read-only rather than disabled: a disabled field drops out of the submitted values.
                  readOnly={Boolean(message)}
                  className={cn("font-mono", message && "bg-surface-sunken text-text-muted")}
                  autoComplete="off"
                  placeholder="callback_en"
                  invalid={Boolean(errors.slug)}
                  {...form.register("slug")}
                />
              </Field>
            </div>

            {pickTenant && (
              <Field
                label="Tenant"
                htmlFor="voicemail-tenant"
                required
                error={errors.tenant_id?.message}
                hint="Each tenant has a library of its own, and its messages play files from its own audio store."
              >
                <Select
                  value={values.tenant_id?.toString() ?? ""}
                  onValueChange={(v) => set("tenant_id", Number(v))}
                >
                  <SelectTrigger id="voicemail-tenant" invalid={Boolean(errors.tenant_id)}>
                    <SelectValue placeholder="Choose a tenant" />
                  </SelectTrigger>
                  <SelectContent>
                    {(tenants ?? []).map((t) => (
                      <SelectItem key={t.id} value={String(t.id)}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}

            <Field
              label="Recording"
              htmlFor="voicemail-media"
              required
              error={errors.media?.message}
              hint="What plays to the lead's voicemail: a file from the tenant's audio store."
            >
              <AudioPicker
                value={values.media}
                onChange={(v) => set("media", v)}
                emptyLabel="No recording chosen"
              />
            </Field>

            <Field
              label="Who it is for"
              htmlFor="voicemail-stage"
              hint="A lead's stage. The rep's phone leads with the message that matches the person they reached; leave it unset for the message everyone else hears."
            >
              <Select
                value={values.stage || NONE}
                onValueChange={(v) => set("stage", v === NONE ? "" : v)}
              >
                <SelectTrigger id="voicemail-stage" className="sm:w-60">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE} description="The message for any lead with no message of their own.">
                    Everyone
                  </SelectItem>
                  {stageSlugs().map((code) => (
                    <SelectItem key={code} value={code} description={stageText(code).description}>
                      {stageText(code).label}
                    </SelectItem>
                  ))}
                  {values.stage && !isCurrentStage(values.stage) && (
                    <SelectItem value={values.stage}>{stageText(values.stage).label}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </Field>

            <Field
              label="Language"
              htmlFor="voicemail-language"
              hint="Shown beside the message on a rep's phone, so they leave the one in the lead's language."
            >
              <Select
                value={values.language || NONE}
                onValueChange={(v) => set("language", v === NONE ? "" : v)}
              >
                <SelectTrigger id="voicemail-language" className="sm:w-60">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Not set</SelectItem>
                  {VOICEMAIL_LANGUAGES.map((code) => (
                    <SelectItem key={code} value={code}>
                      {languageText(code)}
                    </SelectItem>
                  ))}
                  {values.language && !knownLanguage && (
                    <SelectItem value={values.language}>{languageText(values.language)}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </Field>
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="default" loading={form.formState.isSubmitting}>
              {message ? "Save changes" : "Add message"}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}
